import { Activity, Database, ServerCog, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { getApiHealth, getDataStatus, getPlans, getProviderHealth } from "../../data/api";
import type { ProviderHealthRecord } from "../../types";

interface HealthState {
  apiOk: boolean;
  environment: string;
  latestUpdate: string | null;
  planCount: number;
  providers: ProviderHealthRecord[];
  error?: string;
}

const initialState: HealthState = {
  apiOk: false,
  environment: "checking",
  latestUpdate: null,
  planCount: 0,
  providers: []
};

export default function SystemHealth() {
  const [health, setHealth] = useState<HealthState>(initialState);
  const [checkedAt, setCheckedAt] = useState("");

  const refresh = async () => {
    setHealth((current) => ({ ...current, environment: "checking", error: undefined }));
    try {
      const [api, data, plans, providerPayload] = await Promise.all([
        getApiHealth(),
        getDataStatus(),
        getPlans(),
        getProviderHealth()
      ]);
      setHealth({
        apiOk: api.ok,
        environment: api.environment,
        latestUpdate: data.latestUpdate,
        planCount: plans.length,
        providers: providerPayload.health
      });
      setCheckedAt(new Date().toLocaleString());
    } catch (error) {
      setHealth((current) => ({
        ...current,
        apiOk: false,
        environment: "unavailable",
        error: error instanceof Error ? error.message : "Health check failed"
      }));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const providerSuccess = health.providers.filter((provider) => provider.status === "success").length;

  return (
    <section className="route-page health-page">
      <div className="page-head compact-head">
        <div>
          <p className="eyebrow">System Health</p>
          <h2>API, D1 persistence, providers, and market data integrity</h2>
          <span>Operational checks reflect server responses rather than browser-only state.</span>
        </div>
        <button className="btn primary" type="button" onClick={() => void refresh()}><Activity /> Refresh checks</button>
      </div>

      <div className="execution-strip health-strip">
        <HealthStat icon={<ServerCog />} label="Worker API" value={health.apiOk ? "Operational" : "Unavailable"} tone={health.apiOk ? "good" : "bad"} />
        <HealthStat icon={<Database />} label="D1 Plans" value={`${health.planCount} loaded`} tone={health.planCount ? "good" : "warn"} />
        <HealthStat icon={<Activity />} label="Market Providers" value={`${providerSuccess}/${health.providers.length} healthy`} tone={providerSuccess ? "good" : "warn"} />
        <HealthStat icon={<ShieldCheck />} label="Market Data" value={health.latestUpdate ? "Available" : "Unavailable"} tone={health.latestUpdate ? "good" : "warn"} />
      </div>

      {health.error && <div className="secure-note health-error">{health.error}</div>}

      <div className="health-grid">
        <section className="section compact-section">
          <div className="section-subhead">Runtime & Data</div>
          <HealthRow label="Environment" value={health.environment} />
          <HealthRow label="Latest market update" value={health.latestUpdate ?? "Unavailable"} />
          <HealthRow label="Last health check" value={checkedAt || "Checking..."} />
        </section>
        <section className="section compact-section">
          <div className="section-subhead">Provider Status</div>
          {health.providers.map((provider) => <HealthRow key={provider.provider} label={provider.provider} value={provider.status} />)}
          {!health.providers.length && <HealthRow label="Providers" value="No health records" />}
        </section>
      </div>
    </section>
  );
}

function HealthStat({ icon, label, value, tone }: { icon: ReactNode; label: string; value: string; tone: "good" | "warn" | "bad" }) {
  return <div className={`exec-stat health-stat ${tone}`}><span>{icon}{label}</span><b>{value}</b></div>;
}

function HealthRow({ label, value }: { label: string; value: string }) {
  return <div className="health-row"><span>{label}</span><b>{value}</b></div>;
}
